import type { FileManagerRef } from 'devextreme-react/file-manager';
import type { MountRoute } from './createFileBrowserProvider';
import { readShellLayoutState } from '../shell/useShellLayout';
import {
  clipboardPasteHint,
  getClipboardState,
  JARVIS_CLIPBOARD_COPY,
  JARVIS_CUT,
  JARVIS_PASTE,
  canPasteIntoDirectory,
  hasClipboardItems
} from './fileBrowserClipboard';
import { JARVIS_COPY_TO, JARVIS_MOVE_TO } from './fileBrowserDestination';
import { resolveSelectionWriteAccess } from './fileBrowserMountAccess';
import { JARVIS_SHARE } from './fileBrowserShare';
import {
  type BrowserItemFlags,
  DOC_TOOLBAR_EDIT_EXCEL,
  DOC_TOOLBAR_EDIT_WORD,
  DOC_TOOLBAR_PREVIEW,
  JARVIS_ZIP,
  JARVIS_ZIP_ICON,
  MICROSOFT_365_OPEN,
  MEDIA_TOOLBAR_OPEN,
  MEDIA_TOOLBAR_PREVIEW,
  PDF_TOOLBAR_PREVIEW,
  canOpenInMicrosoft365,
  mediaFlagsForItem
} from './mediaBrowserActions';

export { resolveDirectoryWriteAccess, resolveSelectionWriteAccess } from './fileBrowserMountAccess';

export interface FileSelectionToolbarOptions {
  canWrite: boolean;
  canPaste: boolean;
  canShare: boolean;
  allowOfficeEdit?: boolean;
  /** When false (Public read-only), hide clipboard copy. */
  allowClipboardCopy?: boolean;
  /** When false (Public read-only), hide Preview / Open viewer actions. */
  allowPreviewOpen?: boolean;
  flags: BrowserItemFlags;
  canOpenMicrosoft365?: boolean;
  allowContentAccess?: boolean;
}

export function buildFileSelectionToolbarItems({
  canWrite,
  canPaste,
  canShare,
  allowOfficeEdit = true,
  allowClipboardCopy = true,
  allowPreviewOpen = true,
  flags,
  canOpenMicrosoft365 = false,
  allowContentAccess = true
}: FileSelectionToolbarOptions) {
  const showMedia = allowPreviewOpen && (flags.image || flags.video);
  const showPdf = allowPreviewOpen && flags.pdf;
  const showOffice = allowPreviewOpen && (flags.spreadsheet || flags.richedit);
  const showPreviewGroup = showMedia || showPdf || showOffice;
  const clipboardReady = hasClipboardItems();
  const showClipboardCopy = allowClipboardCopy;

  return [
    {
      name: JARVIS_COPY_TO,
      widget: 'dxButton',
      visible: allowContentAccess,
      options: {
        icon: 'copy',
        text: 'Copy to',
        stylingMode: 'text',
        hint: 'Copy selected items to another folder'
      },
      showText: 'inMenu'
    },
    {
      name: JARVIS_MOVE_TO,
      widget: 'dxButton',
      visible: canWrite,
      options: {
        icon: 'movetofolder',
        text: 'Move to',
        stylingMode: 'text',
        hint: 'Move selected items to another folder'
      },
      showText: 'inMenu'
    },
    {
      name: JARVIS_ZIP,
      widget: 'dxButton',
      visible: canWrite,
      options: {
        icon: JARVIS_ZIP_ICON,
        text: 'Zip',
        stylingMode: 'text',
        hint: 'Create a zip archive from the selection'
      },
      showText: 'inMenu'
    },
    { name: 'separator', visible: canWrite },
    {
      name: 'rename',
      widget: 'dxButton',
      visible: canWrite,
      options: { icon: 'rename', stylingMode: 'text', hint: 'Rename' },
      showText: 'inMenu'
    },
    {
      name: JARVIS_CUT,
      widget: 'dxButton',
      visible: canWrite,
      options: { icon: 'cut', stylingMode: 'text', hint: 'Cut selected items (Ctrl+X)' },
      showText: 'inMenu'
    },
    {
      name: JARVIS_CLIPBOARD_COPY,
      widget: 'dxButton',
      visible: showClipboardCopy,
      options: { icon: 'copy', stylingMode: 'text', hint: 'Copy selected items (Ctrl+C)' },
      showText: 'inMenu'
    },
    {
      name: JARVIS_PASTE,
      widget: 'dxButton',
      visible: canWrite && (canPaste || clipboardReady),
      disabled: !canPaste || !clipboardReady,
      options: { icon: 'paste', stylingMode: 'text', hint: clipboardPasteHint(getClipboardState()) },
      showText: 'inMenu'
    },
    {
      name: 'delete',
      widget: 'dxButton',
      visible: canWrite,
      options: { icon: 'trash', stylingMode: 'text', hint: 'Delete' },
      showText: 'inMenu'
    },
    { name: 'separator', visible: canWrite || showClipboardCopy },
    {
      name: 'download',
      widget: 'dxButton',
      visible: allowContentAccess,
      options: { icon: 'download', stylingMode: 'text', hint: 'Download' },
      showText: 'inMenu'
    },
    {
      name: JARVIS_SHARE,
      widget: 'dxButton',
      visible: canShare,
      options: {
        icon: 'share',
        text: 'Share',
        stylingMode: 'text',
        hint: 'Share a link to this file'
      },
      showText: 'inMenu'
    },
    {
      name: MICROSOFT_365_OPEN,
      widget: 'dxButton',
      visible: canOpenMicrosoft365,
      options: {
        icon: 'export',
        text: 'Open in Microsoft 365',
        stylingMode: 'text',
        hint: 'Open the OneDrive file in Microsoft 365'
      },
      showText: 'always'
    },
    { name: 'separator', visible: showPreviewGroup },
    {
      name: MEDIA_TOOLBAR_PREVIEW,
      widget: 'dxButton',
      visible: showMedia,
      options: {
        text: 'Preview',
        icon: 'find',
        stylingMode: 'text',
        hint: 'Preview image or video in a popup'
      },
      showText: 'always'
    },
    {
      name: MEDIA_TOOLBAR_OPEN,
      widget: 'dxButton',
      visible: showMedia,
      options: {
        text: 'Open viewer',
        icon: 'export',
        stylingMode: 'text',
        hint: 'Open in the full viewer (Workspace tab)'
      },
      showText: 'always'
    },
    {
      name: PDF_TOOLBAR_PREVIEW,
      widget: 'dxButton',
      visible: showPdf,
      options: {
        text: 'Viewer',
        icon: 'pdffile',
        stylingMode: 'text',
        hint: 'Open PDF in the popup Viewer'
      },
      showText: 'always'
    },
    {
      name: DOC_TOOLBAR_PREVIEW,
      widget: 'dxButton',
      visible: showOffice,
      options: {
        text: 'Viewer',
        icon: 'doc',
        stylingMode: 'text',
        hint: 'Open document in the popup Viewer'
      },
      showText: 'always'
    },
    {
      name: DOC_TOOLBAR_EDIT_EXCEL,
      widget: 'dxButton',
      visible: showOffice && flags.spreadsheet && canWrite && allowOfficeEdit,
      options: {
        text: 'Open in Editor',
        icon: 'xlsxfile',
        stylingMode: 'text',
        hint: 'Open editable in Workspace'
      },
      showText: 'always'
    },
    {
      name: DOC_TOOLBAR_EDIT_WORD,
      widget: 'dxButton',
      visible: showOffice && flags.richedit && canWrite && allowOfficeEdit,
      options: {
        text: 'Open in Editor',
        icon: 'docxfile',
        stylingMode: 'text',
        hint: 'Open editable in Workspace'
      },
      showText: 'always'
    },
    {
      name: 'clearSelection',
      widget: 'dxButton',
      location: 'after',
      options: { icon: 'remove', stylingMode: 'text', hint: 'Clear selection' },
      showText: 'inMenu'
    }
  ];
}

export function syncFileSelectionToolbar(
  fileManagerRef: { current: FileManagerRef | null },
  _canWrite: boolean,
  routes: readonly MountRoute[],
  allowOfficeEdit = readShellLayoutState().canEditOfficeDocuments
): void {
  const instance = fileManagerRef.current?.instance();
  if (!instance) return;

  const selected = instance.getSelectedItems?.() ?? [];
  const flags: BrowserItemFlags =
    selected.length === 1
      ? mediaFlagsForItem(selected[0])
      : { image: false, video: false, spreadsheet: false, richedit: false, pdf: false };

  const currentDirectory = instance.getCurrentDirectory?.() ?? null;
  const currentPath = currentDirectory?.path ?? '';
  const { canWrite: canWriteDir, isUnavailable } = resolveSelectionWriteAccess(
    currentPath,
    selected,
    routes
  );

  const canPaste = canPasteIntoDirectory(currentDirectory, routes, canWriteDir);

  instance.option(
    'toolbar.fileSelectionItems',
    buildFileSelectionToolbarItems({
      canWrite: canWriteDir,
      canPaste,
      canShare: selected.length === 1 && !selected[0]?.isDirectory && !isUnavailable,
      canOpenMicrosoft365: selected.length === 1 && canOpenInMicrosoft365(selected[0], routes),
      allowOfficeEdit,
      allowClipboardCopy: !isUnavailable,
      allowPreviewOpen: !isUnavailable,
      allowContentAccess: !isUnavailable,
      flags
    })
  );
}

export function scheduleFileSelectionToolbarSync(
  fileManagerRef: { current: FileManagerRef | null },
  canWrite: boolean,
  routes: readonly MountRoute[],
  allowOfficeEdit = readShellLayoutState().canEditOfficeDocuments
): void {
  requestAnimationFrame(() => {
    requestAnimationFrame(() => {
      syncFileSelectionToolbar(fileManagerRef, canWrite, routes, allowOfficeEdit);
    });
  });
}
